import NoiseType from "./NoiseType";

declare global {
  interface Window {
    AudioContext: typeof AudioContext;
    webkitAudioContext: typeof AudioContext;
  }
}

const createNoiseBuffer = (audioContext: AudioContext, fill: (output: Float32Array) => void) => {
  const bufferSize = 4 * audioContext.sampleRate;
  const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
  fill(buffer.getChannelData(0));
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  return source; // caller is responsible for calling start()
};

const createWhiteNoise = (audioContext: AudioContext) =>
  createNoiseBuffer(audioContext, (output) => {
    for (let i = 0; i < output.length; i++) {
      output[i] = Math.random() * 2 - 1;
    }
  });

const createPinkNoise = (audioContext: AudioContext) =>
  createNoiseBuffer(audioContext, (output) => {
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < output.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0707920;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5  - white * 0.0168980;
      output[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
  });

const createBrownNoise = (audioContext: AudioContext) =>
  createNoiseBuffer(audioContext, (output) => {
    let lastOut = 0;
    for (let i = 0; i < output.length; i++) {
      const white = Math.random() * 2 - 1;
      output[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = output[i];
      output[i] *= 3.5;
    }
  });

class NoiseGenerator {
  audioContext: AudioContext
  gainNode: GainNode
  whiteNoise: AudioBufferSourceNode
  pinkNoise: AudioBufferSourceNode
  brownNoise: AudioBufferSourceNode
  volume: number
  isPlaying: boolean
  noiseType: NoiseType | null
  private sourcesStarted: boolean

  constructor(initialVolume: number, initialNoiseType: NoiseType) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContext();
    this.audioContext.resume();

    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = 0;
    this.gainNode.connect(this.audioContext.destination);

    this.whiteNoise = createWhiteNoise(this.audioContext);
    this.pinkNoise = createPinkNoise(this.audioContext);
    this.brownNoise = createBrownNoise(this.audioContext);

    this.volume = initialVolume;
    this.isPlaying = false;
    this.sourcesStarted = false;
    this.noiseType = null;
    this.setNoiseType(initialNoiseType);
  }

  play() {
    // Defer start() until play() so it runs after audioContext.resume() has resolved.
    // iOS Safari silently drops start() calls made on a still-suspended context.
    if (!this.sourcesStarted) {
      this.whiteNoise.start(0);
      this.pinkNoise.start(0);
      this.brownNoise.start(0);
      this.sourcesStarted = true;
    }
    this.gainNode.gain.value = this.volume;
    this.isPlaying = true;
  }

  stop() {
    this.gainNode.gain.value = 0;
    this.isPlaying = false;
  }

  setVolume(volume: number) {
    this.volume = volume;
    if (this.isPlaying) {
      this.gainNode.gain.value = this.volume;
    }
  }

  setNoiseType(noiseType: NoiseType | null) {
    if (this.noiseType !== null) {
      switch (this.noiseType) {
        case NoiseType.White: this.whiteNoise.disconnect(this.gainNode); break;
        case NoiseType.Pink:  this.pinkNoise.disconnect(this.gainNode);  break;
        case NoiseType.Brown: this.brownNoise.disconnect(this.gainNode); break;
      }
    }

    this.noiseType = noiseType;
    if (this.noiseType === null) return;

    switch (this.noiseType) {
      case NoiseType.White: this.whiteNoise.connect(this.gainNode); break;
      case NoiseType.Pink:  this.pinkNoise.connect(this.gainNode);  break;
      case NoiseType.Brown: this.brownNoise.connect(this.gainNode); break;
    }
  }
}

export default NoiseGenerator;
